import React, { useState, useMemo } from 'react';
import { IRootState } from '../../../@types/redux';
import { connect } from 'react-redux';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Icon from '@material-ui/core/Icon';
import { ITransformer } from '../../../@types/model/transformer/transformer';
import Button from '@material-ui/core/Button';
import transformerFunctions from '../../../store/transformer/functions';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import FormHelperText from '@material-ui/core/FormHelperText';
import { EP_REGEX } from '../../../appConstants';
import ProjectDialog from '../../customComponentV2/dialog/ProjectDialog';
import { CustomMouseEvent } from '../../../@types/helper';
import { DialogContent, DialogActions } from '@material-ui/core';
import FloatingActionButton from '../../customComponentV2/input/FloatingActionButton';

interface ITransformerNewButtonProps {
    transformers : Array<ITransformer>;
    isLoading : boolean;
}

const TransformerNewButtonComponent = (props : ITransformerNewButtonProps) : React.ReactElement => {
    const [open, setOpen] = useState<boolean>(false);
    const [poleNumber, setPoleNumber] = useState<string>('');
    const [epNumber, setEpNumber] = useState<string>('');

    const onCloseClick = (): void => {
        if (props.isLoading) return;
        setOpen(false);
    };

    const onClose = (event : CustomMouseEvent, reason : string): void => {
        if (props.isLoading) return;
        if (reason && reason === 'backdropClick') return;
        setOpen(false);
    };

    const onAddClick = (): void => {
        const latestBorehole = props.transformers.slice().sort((a, b) => parseInt(b.EPNumber.substr(2)) - parseInt(a.EPNumber.substr(2)))[0];
        const maxNumber = latestBorehole ? parseInt(latestBorehole.EPNumber.substring(2)) + 1 : 1;

        setOpen(true);
        setPoleNumber('');
        setEpNumber(`EP${maxNumber.toString().padStart(3, '0')}`);
    };

    const onEpNumberChangeChange = (event : React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (event.currentTarget.value.length < 2 || event.currentTarget.value.substr(0, 2) !== 'EP') return;
        setEpNumber(event.currentTarget.value);
    };

    const onPoleNumberChange = (event : React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        setPoleNumber(event.currentTarget.value);
    };

    const onSubmit = (event : React.FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        save();
    };

    const save = async (): Promise<void> => {
        if (!EP_REGEX.test(epNumber)) return;

        await transformerFunctions.createTransformer(poleNumber, epNumber);

        setOpen(false);
    };

    const isValid = useMemo<boolean>(
        () => {
            return EP_REGEX.test(epNumber);
        },
        [epNumber],
    );

    return (
        <div>
            <Tooltip title='Create'>
                <div>
                    <FloatingActionButton
                        color={'secondary'}
                        className={`ml10 mr10 mt5 mb5 p20`}
                        onClick={onAddClick}
                        size={'small'}
                    >
                       <Icon fontSize={'small'} className={'cpd'}>add</Icon>
                    </FloatingActionButton>
                </div>
            </Tooltip>
            <ProjectDialog
                isOpen={open}
                onClose={onCloseClick}
                title={`NEW TRANSFORMER - ${epNumber}`}
                isLoadingCircular={props.isLoading}
            >
                <form autoComplete='off' onSubmit={onSubmit} className='fdc flx1 hfill ais'>
                    <DialogContent className='fdr jcfs aifs flx1'>
                        <div className={'flx1 ais p5 mb10 pr20'}>
                            <FormControl fullWidth>
                                <TextField
                                    autoFocus
                                    autoComplete='off'
                                    id='epNumber'
                                    label='Number'
                                    value={epNumber}
                                    onChange={onEpNumberChangeChange}
                                    required
                                    error={!epNumber || epNumber.length < 2 || !parseInt(epNumber.substr(2))}
                                />
                                {
                                    !epNumber &&
                                    <FormHelperText error>Required</FormHelperText>
                                }
                                {
                                    (epNumber.length < 2 || !parseInt(epNumber.substr(2))) &&
                                    <FormHelperText error>Invalid</FormHelperText>
                                }
                            </FormControl>
                        </div>
                        <div className={'flx1 ais p5 mb10 pr20'}>
                            <FormControl fullWidth>
                                <TextField
                                    autoFocus
                                    autoComplete='off'
                                    id='poleNumber'
                                    label='Pole Number'
                                    value={poleNumber}
                                    onChange={onPoleNumberChange}
                                    className={'TextField'}
                                />
                            </FormControl>
                        </div>
                    </DialogContent>
                    <DialogActions>
                        <Button variant='contained' type='submit' color='primary' disabled={props.isLoading || !isValid}>
                            CREATE
                        </Button>
                        <Button variant='outlined' onClick={onCloseClick} color='primary' disabled={props.isLoading}>
                            CLOSE
                        </Button>
                    </DialogActions>
                </form>
            </ProjectDialog>
        </div>
    );
};

const mapStateToProps = (state : IRootState) => {
    return {
        transformers: state.transformer.transformers,
        isLoading: state.transformer.loading,
    };
};

const TransformerNewButton = connect(
    mapStateToProps,
)(TransformerNewButtonComponent);

export default TransformerNewButton;
